import { useEffect } from "react";
import { Layout } from "@/components/layout";
import { Plus } from "lucide-react";
import { motion } from "framer-motion";
import { container } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { TemplatesEmpty } from "@/components/ui/templates/empty-templates";
import { TemplatesList } from "@/components/ui/templates/list-templates";
import { LibraryList } from "@/components/ui/templates/list-library";
import { useTemplateStore } from "@/store/templates.store";
import { useNavigate } from "@tanstack/react-router";
import { useAuthStore } from "@/store/auth.store";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { SubscriptionOnlyButton } from "@/components/ui/SubscriptionOnlyButton/SubscriptionOnlyButton";
import { ValidationSuscriptionButton } from "@/common/enums/enums";
import HashTabs from "@/components/ui/HashTabs";

export const Templates = (): JSX.Element => {
  const { t } = useTranslation();
  const { loading, templates, fetchAndSetTemplates } = useTemplateStore();
  const { userData } = useAuthStore.getState();
  const navigate = useNavigate();

  useEffect(() => {
    if (userData?.id) {
      void fetchAndSetTemplates({ userId: userData.id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const MyTemplatesContent = (): JSX.Element => {
    if (templates.length === 0 && !loading) {
      return <TemplatesEmpty />;
    }

    if (loading) {
      return (
        <div className="flex justify-center mt-20">
          <LoadingSpinner size={8} />
        </div>
      );
    }

    if (templates.length > 0) {
      return (
        <motion.div
          animate="show"
          className="mx-auto max-w-5xl space-y-8"
          initial="hidden"
          variants={container}
        >
          <div className="p-4">
            <div className="flex justify-end mb-6">
              <div className="flex items-center">
                <SubscriptionOnlyButton
                  validations={[ValidationSuscriptionButton.PLAN_FREE_TRIAL]}
                  onClick={async () => {
                    await navigate({ to: "/template/new" });
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t("templates.newTemplateButton")}
                </SubscriptionOnlyButton>
              </div>
            </div>
            <TemplatesList templates={templates} />
          </div>
        </motion.div>
      );
    }

    return <div />;
  };

  const LibraryContent = (): JSX.Element => {
    return (
      <motion.div
        animate="show"
        className="mx-auto max-w-5xl space-y-8"
        initial="hidden"
        variants={container}
      >
        <div className="p-4">
          <LibraryList useLazyLoading />
        </div>
      </motion.div>
    );
  };

  return (
    <Layout
      navbarTitle={t("templates.title")}
      breadcrumbs={[
        { name: t("templates.title"), href: `/templates`, current: true },
      ]}
    >
      <div className="h-full bg-background p-6">
        <HashTabs
          tabs={[
            {
              value: "my-templates",
              label: t("templates.myTemplatesTab"),
              content: <MyTemplatesContent />,
            },
            {
              value: "library",
              label: t("templates.libraryTab"),
              content: <LibraryContent />,
            },
          ]}
        />
      </div>
    </Layout>
  );
};
